/**
 * リトライメカニズム
 * 指数バックオフ、ジッター、条件的リトライを実装
 */

export interface RetryConfig {
  maxAttempts: number; // 最大試行回数
  baseDelay: number; // 基本遅延時間(ms)
  maxDelay: number; // 最大遅延時間(ms)
  backoffMultiplier: number; // バックオフ倍率
  jitterMax: number; // ジッター最大値(ms)
  retryCondition?: (error: any) => boolean; // リトライ条件
}

export interface RetryStats {
  attempts: number;
  totalDelay: number;
  lastError?: Error;
  success: boolean;
}

export class RetryMechanism {
  constructor(private config: RetryConfig) {}

  /**
   * リトライ実行
   */
  async execute<T>(operation: () => Promise<T>): Promise<T> {
    const stats: RetryStats = {
      attempts: 0,
      totalDelay: 0,
      success: false,
    };

    let lastError: Error;

    for (let attempt = 1; attempt <= this.config.maxAttempts; attempt++) {
      stats.attempts = attempt;

      try {
        console.log(`🔄 Retry attempt ${attempt}/${this.config.maxAttempts}`);
        const result = await operation();
        stats.success = true;

        if (attempt > 1) {
          console.log(`✅ Retry succeeded on attempt ${attempt}`);
        }

        return result;
      } catch (error) {
        lastError = error instanceof Error ? error : new Error(String(error));
        stats.lastError = lastError;

        console.log(`❌ Attempt ${attempt} failed:`, lastError.message);

        // 最後の試行なら例外を投げる
        if (attempt === this.config.maxAttempts) {
          break;
        }

        // リトライ条件をチェック
        if (this.config.retryCondition && !this.config.retryCondition(lastError)) {
          console.log('⛔ Retry condition not met, aborting retries');
          break;
        }

        // 遅延時間を計算して待機
        const delay = this.calculateDelay(attempt);
        stats.totalDelay += delay;

        console.log(`⏳ Waiting ${delay}ms before next attempt...`);
        await this.sleep(delay);
      }
    }

    // すべてのリトライが失敗
    throw new Error(
      `All ${stats.attempts} retry attempts failed. Last error: ${lastError.message}`
    );
  }

  /**
   * 遅延時間を計算（指数バックオフ + ジッター）
   */
  private calculateDelay(attempt: number): number {
    // 指数バックオフ
    const exponentialDelay =
      this.config.baseDelay * Math.pow(this.config.backoffMultiplier, attempt - 1);

    // 最大遅延時間を適用
    const cappedDelay = Math.min(exponentialDelay, this.config.maxDelay);

    // ジッター追加（雷鳴群集効果を防ぐ）
    const jitter = Math.random() * this.config.jitterMax;

    return Math.floor(cappedDelay + jitter);
  }

  /**
   * 待機処理
   */
  private sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}

/**
 * デフォルト設定でのリトライメカニズム作成
 */
export function createRetryMechanism(customConfig?: Partial<RetryConfig>): RetryMechanism {
  const defaultConfig: RetryConfig = {
    maxAttempts: 3,
    baseDelay: 1000, // 1秒
    maxDelay: 10000, // 10秒
    backoffMultiplier: 2, // 指数: 1s, 2s, 4s, 8s...
    jitterMax: 500, // 最大500msのランダム遅延
    retryCondition: (error) => {
      // デフォルトのリトライ条件
      if (error?.message) {
        // リトライしない条件
        const nonRetryableErrors = [
          'カメラの使用が許可されていません',
          'カメラデバイスが見つかりません',
          'セキュリティ上の理由',
          'サポートされていません',
        ];

        return !nonRetryableErrors.some((msg) => error.message.includes(msg));
      }
      return true;
    },
  };

  const config = { ...defaultConfig, ...customConfig };
  return new RetryMechanism(config);
}

/**
 * 簡易リトライ関数（直接使用可能）
 */
export async function retry<T>(
  operation: () => Promise<T>,
  config?: Partial<RetryConfig>
): Promise<T> {
  const retryMechanism = createRetryMechanism(config);
  return retryMechanism.execute(operation);
}

/**
 * 条件的リトライのプリセット
 */
export const RetryPresets = {
  /**
   * ネットワークエラー用
   */
  NETWORK: {
    maxAttempts: 3,
    baseDelay: 1000,
    maxDelay: 5000,
    backoffMultiplier: 2,
    jitterMax: 300,
    retryCondition: (error: any) => {
      const retryableNetworkErrors = [
        'NetworkError',
        'fetch',
        'timeout',
        'connection',
        'ECONNRESET',
        'ETIMEDOUT',
      ];
      return retryableNetworkErrors.some((keyword) =>
        error?.message?.toLowerCase().includes(keyword.toLowerCase())
      );
    },
  },

  /**
   * ファイルアップロード用
   */
  UPLOAD: {
    maxAttempts: 5,
    baseDelay: 2000,
    maxDelay: 30000,
    backoffMultiplier: 1.5,
    jitterMax: 1000,
    retryCondition: (error: any) => {
      const retryableUploadErrors = ['upload failed', 'temporary', '一時的', '503', '502', '500'];
      return retryableUploadErrors.some((keyword) =>
        error?.message?.toLowerCase().includes(keyword.toLowerCase())
      );
    },
  },

  /**
   * カメラアクセス用
   */
  CAMERA: {
    maxAttempts: 3,
    baseDelay: 1500,
    maxDelay: 5000,
    backoffMultiplier: 1.8,
    jitterMax: 500,
    retryCondition: (error: any) => {
      const retryableCameraErrors = [
        'カメラが他のアプリケーションで使用中',
        '初期化に失敗',
        'NotReadableError',
        'AbortError',
      ];
      return retryableCameraErrors.some((keyword) => error?.message?.includes(keyword));
    },
  },
};
